/* The Venues destination — STORY-013.
 *
 * Every Venue in the workspace with its zone and how many Events are held
 * there, plus the availability switch that decides whether it is offered on new
 * Events.
 *
 * Unavailable ones are LISTED here, dimmed AND labelled. Dimmed alone survives
 * neither greyscale nor a screen reader — `design.md` — and this is also the
 * only screen where one can be turned back on, so hiding them would be a
 * one-way door.
 */

import type { Venue } from '@eventuley/contracts';
import { zoneCity, zoneOffset } from '@eventuley/i18n';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useCurrentUser } from '../../auth/index.js';
import { useEnabledValues } from '../../organisations/index.js';
import { useCreateVenue, useDeleteVenue, useUpdateVenue, useVenues } from '../api/index.js';
import { TimezoneSelect } from '../components/timezone-select.js';

export function VenuesScreen() {
  const t = useTranslate();
  const { organisationId = '' } = useParams({ strict: false });
  const { data, error, isPending } = useVenues(organisationId);
  const { data: user } = useCurrentUser();
  const { data: enabled } = useEnabledValues(organisationId);
  const create = useCreateVenue(organisationId);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');

  const venues = data?.venues ?? [];
  const enabledZones = (enabled?.values ?? [])
    .filter((row) => row.kind === 'timezone')
    .map((row) => row.value);
  const isOwner =
    user?.memberships.find((held) => held.organisationId === organisationId)?.role === 'owner';

  return (
    <>
      <div className="page-head">
        <h1>{t('venues.title')}</h1>
        {venues.length > 0 && !adding ? (
          <Button onClick={() => setAdding(true)}>{t('venues.add_new')}</Button>
        ) : null}
      </div>

      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      {isPending ? <p className="field__hint">{t('common.loading')}</p> : null}

      {adding ? (
        <section className="settings__section">
          <h2>{t('venues.add_heading')}</h2>
          <form
            onSubmit={(submitted) => {
              submitted.preventDefault();
              create.mutate(
                { name, ...(timezone === '' ? {} : { timezone }) },
                {
                  onSuccess: () => {
                    setName('');
                    setTimezone('');
                    setAdding(false);
                  },
                },
              );
            }}
            noValidate
          >
            <TextField
              label={t('venues.name_label')}
              value={name}
              onChange={(changed) => setName(changed.target.value)}
              error={
                create.error instanceof ApiError && create.error.code === 'validation_failed'
                  ? t('venues.name_required')
                  : undefined
              }
            />
            <TimezoneSelect
              label={t('venues.timezone_label')}
              value={timezone}
              enabled={enabledZones}
              canManage={Boolean(isOwner)}
              onChange={setTimezone}
            />

            {create.error && !(create.error instanceof ApiError) ? (
              <Callout tone="danger">
                {create.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
              </Callout>
            ) : null}

            <div className="btn-row">
              <Button type="submit" busy={create.isPending}>
                {t('venues.add_submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  create.reset();
                  setAdding(false);
                }}
              >
                {t('venues.add_cancel')}
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      {!isPending && venues.length === 0 && !adding && !error ? (
        <section className="settings__section">
          <h2>{t('venues.empty.title')}</h2>
          {/* Says a Venue can also be made while writing an Event, so nobody
              feels obliged to set them all up first — STORY-013. */}
          <p className="field__hint">{t('venues.empty.body')}</p>
          <Button onClick={() => setAdding(true)}>{t('venues.empty.action')}</Button>
        </section>
      ) : null}

      {venues.length > 0 ? (
        <ul className="row-list">
          {venues.map((venue) => (
            <VenueRow
              key={venue.id}
              venue={venue}
              organisationId={organisationId}
              enabledZones={enabledZones}
              isOwner={Boolean(isOwner)}
            />
          ))}
        </ul>
      ) : null}
    </>
  );
}

function VenueRow({
  venue,
  organisationId,
  enabledZones,
  isOwner,
}: {
  venue: Venue;
  organisationId: string;
  enabledZones: readonly string[];
  isOwner: boolean;
}) {
  const t = useTranslate();
  const update = useUpdateVenue(organisationId);
  const remove = useDeleteVenue(organisationId);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(venue.name);
  const [timezone, setTimezone] = useState(venue.timezone ?? '');

  /* Opening and closing the editor both reset the draft from the record and
   * clear the mutation. These initialisers run once and the row is keyed by
   * id, so without this a rejected rename and its error both survive Cancel:
   * reopening showed the refused name and the old message, and saving again
   * repeated the same 409. */
  const openEditor = () => {
    setName(venue.name);
    setTimezone(venue.timezone ?? '');
    update.reset();
    setEditing(true);
  };

  const closeEditor = () => {
    setName(venue.name);
    setTimezone(venue.timezone ?? '');
    update.reset();
    setEditing(false);
  };

  /* The count comes from the server rather than the message, so the client
   * builds its own sentence — `coding.md` branches on the code and never
   * parses a message. */
  const inUse =
    remove.error instanceof ApiError && remove.error.code === 'conflict'
      ? Number(remove.error.fields.find((field) => field.path === 'eventCount')?.rule ?? 0)
      : null;

  return (
    <li className={venue.available ? 'row' : 'row row--muted'}>
      <span className="row__main">
        <span className="row__title">{venue.name}</span>
        <span className="row__meta">
          {venue.timezone
            ? `${zoneCity(venue.timezone)} · ${zoneOffset(venue.timezone)}`
            : t('venues.timezone_inherited')}
        </span>
        <span className="row__meta">
          {t('venues.event_count', { count: String(venue.eventCount) })}
        </span>

        {/* Dimmed AND labelled. Opacity survives neither greyscale nor a screen
            reader, so the words are what actually carry the state. The badge
            states the consequence rather than repeating the switch beside it —
            two elements reading "Hidden" is one of them saying nothing. */}
        {venue.available ? null : <span className="badge">{t('venues.badge_hidden')}</span>}
      </span>

      <span className="row__actions">
        <label className="enabled-set__toggle">
          <input
            type="checkbox"
            role="switch"
            checked={venue.available}
            aria-checked={venue.available}
            disabled={update.isPending}
            aria-label={t('venues.toggle', { name: venue.name })}
            onChange={() => update.mutate({ venueId: venue.id, available: !venue.available })}
          />
          <span>{venue.available ? t('venues.offered') : t('venues.hidden')}</span>
        </label>

        <Button variant="ghost" onClick={editing ? closeEditor : openEditor}>
          {t('venues.edit')}
        </Button>

        {/* Offered only when nothing uses it. A Venue with Events is hidden
            rather than deleted, and hiding loses nothing. */}
        {venue.eventCount === 0 ? (
          <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate(venue.id)}>
            {t('venues.delete')}
          </Button>
        ) : null}
      </span>

      {/* Outside `.row__main` deliberately: that is a `<span>`, which may not
          contain a callout or a form, and it is a narrow flex column that would
          squeeze one. `.row__expand` gives them the row's full width on their
          own line. */}
      {inUse !== null ? (
        <div className="row__expand">
          <Callout tone="warning">{t('venues.in_use', { count: String(inUse) })}</Callout>
        </div>
      ) : null}

      {editing ? (
        <form
          className="row__expand"
          onSubmit={(submitted) => {
            submitted.preventDefault();
            update.mutate(
              { venueId: venue.id, name, timezone: timezone === '' ? null : timezone },
              { onSuccess: () => setEditing(false) },
            );
          }}
          noValidate
        >
          <TextField
            label={t('venues.name_label')}
            value={name}
            onChange={(changed) => setName(changed.target.value)}
            error={
              update.error instanceof ApiError && update.error.code === 'conflict'
                ? t('venues.name_taken')
                : undefined
            }
          />
          <TimezoneSelect
            label={t('venues.timezone_label')}
            value={timezone}
            enabled={enabledZones}
            canManage={isOwner}
            onChange={setTimezone}
          />
          {/* Changing a zone changes how every past Event here displays.
                Stored instants do not move; only the presentation does — said
                out loud, because it is a surprise otherwise. */}
          <p className="field__hint">{t('venues.timezone_warning')}</p>

          <div className="btn-row">
            <Button type="submit" busy={update.isPending}>
              {t('venues.save')}
            </Button>
            <Button variant="ghost" onClick={closeEditor}>
              {t('venues.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
